import type { ReactNode } from 'react'

import { Fragment, createElement, useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'

export type Locale = 'ar' | 'en'

export const toLocale = (value?: string | string[] | null): Locale => {
    const raw = Array.isArray(value) ? value[0] : value

    return raw === 'en' ? 'en' : 'ar'
}

export const pick = (locale: Locale, ar: string, en: string): string => locale === 'en' ? en : ar

export const interpolate = (template: string, vars: Record<string, ReactNode>): ReactNode => {
    const parts = template.split(/\{(\w+)\}/g)
    const children = parts.map((part, index) => {
        if (index % 2 === 1) return createElement(Fragment, { key: `v-${index}` }, vars[part] ?? '')

        return createElement(Fragment, { key: `t-${index}` }, part)
    })

    return createElement(Fragment, null, ...children)
}

export const fill = (template: string, vars: Record<string, string | number>): string =>
    template.replace(/\{(\w+)\}/g, (match, key: string) => vars[key] !== undefined ? String(vars[key]) : match)

export const localePrefix = (locale: Locale): string => locale === 'en' ? '/en' : ''

export const useLocale = (): Locale => {
    const { locale } = useRouter()

    return toLocale(locale)
}

export const useI18n = () => {
    const locale = useLocale()

    const tx = useCallback((ar: string, en: string): string => pick(locale, ar, en), [locale])

    const txn = useCallback(
        (ar: string, en: string, vars: Record<string, ReactNode>): ReactNode => interpolate(pick(locale, ar, en), vars),
        [locale]
    )

    return useMemo(() => ({
        locale,
        isRtl: locale === 'ar',
        dir: locale === 'ar' ? 'rtl' as const : 'ltr' as const,
        tx,
        txn
    }), [locale, tx, txn])
}
